(() => {
  'use strict';

  const HEADER_SELECTOR = '.section-header';
  const CLONE_CLASS = 'splide__slide--clone';
  const HEADING_SELECTOR = ':is(h1, h2, h3, h4, h5, h6)';
  const SCROLL_THRESHOLD = 20;
  const TRANSPARENT_CLASS = 'header-transparent';

  const truthy = (value) => value === 'true' || value === true;

  /**
   * Splide is loaded with `defer` in theme.liquid, so it may not be available
   * when this file runs. Poll briefly instead of assuming load order.
   */
  const whenSplideReady = () =>
    new Promise((resolve, reject) => {
      if (window.Splide) {
        resolve();
        return;
      }

      let attempts = 0;
      const timer = setInterval(() => {
        if (window.Splide) {
          clearInterval(timer);
          resolve();
        } else if ((attempts += 1) > 100) {
          clearInterval(timer);
          reject(new Error('Splide was not found on the page'));
        }
      }, 50);
    });

  const buildOptions = ({ dataset }) => {
    const isFade = dataset.effect === 'fade';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    return {
      type: isFade ? 'fade' : truthy(dataset.loop) ? 'loop' : 'slide',
      rewind: isFade && truthy(dataset.loop),
      speed: reduceMotion ? 0 : Number(dataset.speed) || 800,
      start: 0,
      perPage: 1,
      perMove: 1,
      arrows: truthy(dataset.arrows),
      pagination: false,
      drag: truthy(dataset.drag),
      autoplay: !reduceMotion && truthy(dataset.autoplay),
      interval: Number(dataset.interval) || 6000,
      pauseOnHover: truthy(dataset.pauseOnHover),
      pauseOnFocus: true,
      resetProgress: false,
      keyboard: 'focused',
      updateOnMove: true,
      classes: {
        arrows: 'splide__arrows custom-hero-slider__arrows',
        arrow: 'splide__arrow custom-hero-slider__arrow',
        prev: 'splide__arrow--prev',
        next: 'splide__arrow--next',
      },
    };
  };

  const createTransparentHeader = (header) => {
    const state = { scrolled: false, hovered: false, focused: false };
    let transparent = null;

    const isScrolled = () => window.scrollY > SCROLL_THRESHOLD;

    const update = () => {
      // querySelector walks the DOM tree, not layout, so this forces no reflow.
      const solid =
        state.scrolled ||
        state.hovered ||
        state.focused ||
        Boolean(header.querySelector('details[open]'));

      if (transparent === !solid) return;

      transparent = !solid;
      document.body.classList.toggle(TRANSPARENT_CLASS, transparent);
    };

    const set = (key, value) => {
      state[key] = value;
      update();
    };

    header.addEventListener('mouseenter', () => set('hovered', true));
    header.addEventListener('mouseleave', () => set('hovered', false));
    header.addEventListener('focusin', () => set('focused', true));
    // focusout fires before the next element gains focus, so relatedTarget is
    // what tells us whether focus is leaving the header entirely.
    header.addEventListener('focusout', ({ relatedTarget }) =>
      set('focused', header.contains(relatedTarget))
    );
    // `toggle` does not bubble, so it has to be caught in the capture phase.
    header.addEventListener('toggle', update, true);

    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;

        requestAnimationFrame(() => {
          ticking = false;
          set('scrolled', isScrolled());
        });
      },
      { passive: true }
    );

    // Exposes the header height so the slider can pull itself up underneath it.
    const syncHeight = () =>
      document.documentElement.style.setProperty(
        '--hero-header-height',
        `${header.offsetHeight}px`
      );

    new ResizeObserver(syncHeight).observe(header);

    syncHeight();
    // Covers reloads that restore a scroll position part-way down the page.
    set('scrolled', isScrolled());
  };

  // Remove duplicated heading tags from Splide's Clones
  const demoteCloneHeadings = (target) => {
    const headings = target.querySelectorAll(`.${CLONE_CLASS} ${HEADING_SELECTOR}`);

    headings.forEach((heading) => {
      const div = document.createElement('div');
      div.className = heading.className;
      div.innerHTML = heading.innerHTML;
      heading.replaceWith(div);
    });
  };

  const init = (root) => {
    if (root.dataset.heroSliderInitialized === 'true') return;

    const target = root.querySelector('.custom-hero-slider__splide');
    const slideCount = target?.querySelectorAll('.splide__slide').length ?? 0;
    if (!slideCount) return;

    const options = buildOptions(root);

    // A single slide needs no arrows, autoplay or dragging.
    if (slideCount === 1) {
      Object.assign(options, { type: 'slide', arrows: false, autoplay: false, drag: false });
    }

    const splide = new window.Splide(target, options);

    // Safety net in case anything lands the loop off slide 1 — jump back
    // without animating.
    splide.on('mounted', () => {
      if (splide.index === 0) return;

      splide.Components.Move.jump(0);
      splide.Components.Controller.setIndex(0);
    });

    // Deferred a frame so Clones has finished (re)generating before we look.
    splide.on('mounted refresh', () => {
      requestAnimationFrame(() => demoteCloneHeadings(target));
    });

    splide.mount();

    let lastWidth = 0;

    new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width);
      if (width === lastWidth) return;

      lastWidth = width;
      splide.emit('resize');
    }).observe(target);

    // After mount, not before: mounting measures every slide, and touching
    // <body> first would turn each of those measurements into a forced layout.
    const header = truthy(root.dataset.transparentHeader)
      ? document.querySelector(HEADER_SELECTOR)
      : null;

    if (header) createTransparentHeader(header);

    root.dataset.heroSliderInitialized = 'true';
  };

  whenSplideReady()
    .then(() => document.querySelectorAll('[data-hero-slider]').forEach(init))
    .catch((error) => console.warn('[custom-hero-slider]', error.message));
})();
